import { useState } from 'react'
import dayjs from 'dayjs'

import AlertOutlinedIcon from '@ant-design/icons/AlertOutlined'
import CalendarOutlinedIcon from '@ant-design/icons/CalendarOutlined'
import ClockCircleOutlinedIcon from '@ant-design/icons/ClockCircleOutlined'
import ExperimentOutlinedIcon from '@ant-design/icons/ExperimentOutlined'
import FileDoneOutlined from '@ant-design/icons/FileDoneOutlined'
import FileExclamationOutlined from '@ant-design/icons/FileExclamationOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import MedicineBoxOutlinedIcon from '@ant-design/icons/MedicineBoxOutlined'
import MoreOutlinedIcon from '@ant-design/icons/MoreOutlined'
import UserAddOutlinedIcon from '@ant-design/icons/UserAddOutlined'
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined'
import PillIcon from '@icons/PillIcon'

import AvatarMui from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import IconButton from '@components/_mui/IconButton'
import Typography from '@components/_mui/Typography'

import { AvailabilityType, SchedTypeLabel, toTitleCase } from '@shared/utils'

import { useTaskTypes } from '@pages/Patients/Patient/ProfileTab/Tasks/Tasks.hooks'
import Avatar from '@components/Avatar'

import {
  useMarkAllRead,
  useNotificationOnClick,
  useNotificationUpdate,
  useUnreadNotificationsCount,
  useUserNotifications,
} from './Notifications.hooks'
import { NotificationType } from './Notifications.utils'

export default function NotificationsPopover({ anchorEl, open = false, onClose }) {
  const [tab, setTab] = useState('unread')

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useUserNotifications({
    read: tab === 'unread' ? false : undefined,
  })

  const unreadNotificationsCount = useUnreadNotificationsCount()

  const markAllRead = useMarkAllRead()
  const update = useNotificationUpdate()

  const showEmpty = !data || data?.length === 0
  const showData = !showEmpty && data?.length > 0

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      slotProps={{
        paper: {
          sx: { width: '440px' },
          'data-testid': 'notifications-popover',
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', px: 3, py: 2 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: '1 1 auto' }}>
          <Typography variant="h4">Notifications</Typography>
          {unreadNotificationsCount && <Chip label={unreadNotificationsCount} color="error" variant="light" size="small" />}
        </Stack>
        <Button
          onClick={() => markAllRead.mutateAsync()}
          loading={markAllRead.isPending}
          size="small"
          sx={{ textTransform: 'none' }}
          data-testid="notifications-mark-clear"
        >
          Mark all as read
        </Button>
      </Stack>
      <Tabs value={tab} onChange={(e, tab) => setTab(tab)} sx={{ px: 2 }}>
        <Tab label="Unread" value="unread" data-testid="notifications-unread-tab" />
        <Tab label="All" value="all" data-testid="notifications-all-tab" />
      </Tabs>
      <Divider />
      {showEmpty && (
        <Box sx={{ p: 2 }} data-testid="notifications-empty">
          <Typography variant="h5" align="center" sx={{ color: 'text.secondary', height: 50, fontWeight: 'normal' }}>
            Nothing to see here!
          </Typography>
        </Box>
      )}
      {showData && (
        <Box sx={{ maxHeight: '360px', overflowY: 'auto' }}>
          <List disablePadding data-testid="notifications-list">
            {data.map((notification, index) => (
              <NotificationItem
                key={notification.id}
                divider={index < data.length - 1}
                notification={notification}
                onUpdate={update.mutate}
                onClose={onClose}
              />
            ))}
            {hasNextPage && (
              <Button
                fullWidth
                loading={isFetchingNextPage}
                onClick={() => fetchNextPage()}
                endIcon={<MoreOutlinedIcon rotate={90} />}
                loadingPosition="end"
                sx={{ textTransform: 'none' }}
                data-testid="notifications-more"
              >
                more
              </Button>
            )}
          </List>
        </Box>
      )}
    </Popover>
  )
}

function NotificationItem({ divider, notification, onUpdate, onClose }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handleMenuOpen = (event) => setAnchorEl(event?.currentTarget)
  const handleMenuClose = () => setAnchorEl(null)

  const onClick = useNotificationOnClick(notification)

  return (
    <ListItemButton
      divider={divider}
      onClick={() => {
        onClick()
        onClose()
      }}
      sx={{
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        transition: 'opacity 0.4s ease-in-out',
        opacity: notification.read ? 0.5 : 1,
        pr: 1,
      }}
      data-testid={`notification-item-${notification.id}`}
    >
      <NotificationContent notification={notification} />
      <IconButton
        component="div"
        onClick={(e) => {
          e.stopPropagation()
          handleMenuOpen(e)
        }}
      >
        <MoreOutlinedIcon />
      </IconButton>
      <Menu
        id={`notification-menu-${notification.id}`}
        anchorEl={anchorEl}
        open={open}
        onClose={(e) => {
          e.stopPropagation()
          handleMenuClose()
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: { 'aria-labelledby': 'notification-menu-button' },
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation()
            onUpdate({ id: notification.id, read: !notification.read })
            handleMenuClose()
          }}
        >
          Mark as {notification.read ? 'unread' : 'read'}
        </MenuItem>
      </Menu>
    </ListItemButton>
  )
}

export function NotificationContent({ notification }) {
  const taskTypes = useTaskTypes()
  const getTaskLabel = (kind) => taskTypes[kind] || kind

  if (!notification) return null

  const config = notificationTypeConfig[notification.type]
  if (!config) return null

  const { icon, getMessage } = config

  const testId = `notification-item-${notification.id}-${notification.type.toLowerCase()}`

  return (
    <Stack direction="row" spacing={1} data-testid={testId}>
      <Box sx={{ pt: 0.5 }}>
        {![
          NotificationType.Message,
          NotificationType.Availabilities,
          NotificationType.CompletedQLinkLabTask,
          NotificationType.ExpiredQLinkResultTask,
          NotificationType.EscalationTaskCreated,
          NotificationType.EscalationTaskReassigned,
          NotificationType.EscalationTaskCompleted,
        ].includes(notification.type) ? (
          <AvatarMui sx={{ backgroundColor: 'background.default', boxShadow: (theme) => theme.customShadows.z2, color: 'text.primary' }}>
            {icon}
          </AvatarMui>
        ) : notification.author ? (
          <Avatar variant="circular" user={notification.author} />
        ) : null}
      </Box>
      <div>
        <Typography variant="body2" data-testid={`${testId}-content`}>
          {getMessage(notification, { getTaskLabel })}
        </Typography>
        {notification.createdAt && (
          <Typography variant="caption" data-testid={`${testId}-date`} sx={{ color: 'text.secondary' }}>
            {dayjs(notification.createdAt).format('MMM D, hh:mm A')}
          </Typography>
        )}
      </div>
    </Stack>
  )
}

const notificationTypeConfig = {
  [NotificationType.Message]: {
    icon: null,
    getMessage: (notification) => (
      <>
        <b>{notification.author?.fullName || 'User'}</b> {notification.properties.replyingMessage ? 'responded to you' : 'mentioned you'} in
        the patient's internal chat (MRN&nbsp;{notification.properties.patientId})
      </>
    ),
  },
  [NotificationType.DoseSpot]: {
    icon: <PillIcon />,
    getMessage: () => 'You have a new DoseSpot notification, click to open',
  },
  [NotificationType.QuestLabQuestionnaire]: {
    icon: <ExperimentOutlinedIcon />,
    getMessage: (notification) => `You have a new Quest lab notification. Click to view lab #${notification.properties.id}`,
  },
  [NotificationType.ReassignedPatient]: {
    icon: <UserAddOutlinedIcon />,
    getMessage: (notification) => (
      <>
        <b>{notification.properties.patientName}</b> MRN {notification.properties.patientId} in <b>{notification.properties.cboName}</b> has
        been reassigned to you
      </>
    ),
  },
  [NotificationType.PatientUpdatedCBO]: {
    icon: <MedicineBoxOutlinedIcon />,
    getMessage: (notification) => (
      <>
        <b>{notification.properties.patientName}</b> MRN {notification.properties.patientId} has been updated to{' '}
        <b>{notification.properties.cboName}</b> CBO
      </>
    ),
  },
  [NotificationType.Availabilities]: {
    icon: <CalendarOutlinedIcon />,
    getMessage: (notification) => {
      const format = 'YYYY-MM-DDTHH:mm'
      const start = dayjs(notification.properties.startTimeTz, format)
      const end = dayjs(notification.properties.endTimeTz, format)
      const type = notification.properties.scheduleSlotType
      const typeMsg =
        type === AvailabilityType.OutOfOffice
          ? 'out of office time'
          : type === AvailabilityType.Admin
            ? 'admin time'
            : 'appointment availability'

      return (
        <>
          <b>{notification.author?.fullName || 'User'}</b> has declined your {typeMsg} request for {start.format('MMM D')} from{' '}
          {start.format('LT')} to {end.format('LT')}
        </>
      )
    },
  },
  [NotificationType.NewAppointment]: {
    icon: <CalendarOutlinedIcon />,
    getMessage: (notification) => {
      const { patientId, patientName, appointmentSchedType, encounterId, encounterDescription } = notification.properties

      return (
        <>
          You have a new <b>{SchedTypeLabel[appointmentSchedType]} Appointment</b> with the <b>{patientName}</b> MRN {patientId} in the{' '}
          {encounterDescription} Encounter #{encounterId}
        </>
      )
    },
  },
  [NotificationType.AppointmentScheduled]: {
    icon: <CalendarOutlinedIcon />,
    getMessage: (notification) => {
      const { patientId, patientName, appointmentType, appointmentTime, encounterId, encounterDescription } = notification.properties

      // appointmentTime carries the provider's UTC offset, parse the wall clock so it reads in their timezone
      const start = dayjs(appointmentTime, 'YYYY-MM-DDTHH:mm')

      return (
        <>
          You have a new <b>{toTitleCase(appointmentType ?? '')} Appointment</b> with <b>{patientName}</b> MRN {patientId} on{' '}
          <b>{start.format('MMM D, YYYY')}</b> at <b>{start.format('LT')}</b>
          {encounterId ? (
            <>
              {' '}
              in the {encounterDescription} Encounter #{encounterId}
            </>
          ) : null}
        </>
      )
    },
  },
  [NotificationType.CompleteScheduledHours]: {
    icon: <ClockCircleOutlinedIcon />,
    getMessage: () =>
      'Please complete adding your availability. Your time inputted is less than 75% of your set max number of weekly hours',
  },
  [NotificationType.CompletedQLinkLabTask]: {
    icon: null,
    getMessage: (notification) => (
      <>
        QLink labs for <b>{notification.properties.patientName}</b> MRN {notification.properties.patientId} have been ordered
      </>
    ),
  },
  [NotificationType.ExpiredQLinkResultTask]: {
    icon: null,
    getMessage: (notification) => (
      <>
        The QLink lab task for <b>{notification.properties.patientName}</b> MRN {notification.properties.patientId} has expired because no
        lab results were completed within 30 days.
      </>
    ),
  },
  [NotificationType.ReportGeneration]: {
    icon: <FileTextOutlined />,
    getMessage: (notification) => (
      <>
        Report generation started: <b>{notification.properties.report}</b>
      </>
    ),
  },
  [NotificationType.ReportCompletion]: {
    icon: <FileDoneOutlined />,
    getMessage: (notification) => (
      <>
        Report generation completed: <b>{notification.properties.report}</b> has been sent to your email
      </>
    ),
  },
  [NotificationType.ReportError]: {
    icon: <FileExclamationOutlined />,
    getMessage: (notification) => (
      <>
        Report generation failed: <b>{notification.properties.report}</b>. {notification.properties.error}
      </>
    ),
  },
  [NotificationType.TaskCreation]: {
    icon: <AssignmentIndOutlinedIcon />,
    getMessage: (notification, { getTaskLabel } = {}) => (
      <>
        New <b>{getTaskLabel(notification.properties.taskKind)}</b> task <b>{notification.properties.taskIdentifier}</b> has been created
        for patient <b>{notification.properties.patientName}</b> (MRN: {notification.properties.patientId})
      </>
    ),
  },
  [NotificationType.LicenseExpiration]: {
    icon: <FileExclamationOutlined />,
    getMessage: (notification) => {
      const { state, type, licenseNumber, medicaid, expirationDate } = notification.properties
      const kindLabel = medicaid ? `Medicaid ${type || 'license'}` : type || 'license'
      const exp = expirationDate ? dayjs(expirationDate).format('MMM D, YYYY') : 'soon'
      return (
        <>
          Your <b>{state}</b> {kindLabel}
          {licenseNumber ? (
            <>
              {' '}
              <b>#{licenseNumber}</b>
            </>
          ) : null}{' '}
          is expiring on <b>{exp}</b>
        </>
      )
    },
  },
  [NotificationType.EscalationTaskCreated]: {
    icon: null,
    getMessage: (notification) => {
      const { reason, description } = notification.properties
      return (
        <>
          <b>{notification.author?.fullName || 'A user'}</b> created an escalation for you{reason ? <>: {reason}</> : null}
          {description ? <> - {description}</> : null}
        </>
      )
    },
  },
  [NotificationType.EscalationTaskReassigned]: {
    icon: null,
    getMessage: (notification) => {
      const { reason, description } = notification.properties
      return (
        <>
          <b>{notification.author?.fullName || 'A user'}</b> reassigned an escalation to you{reason ? <>: {reason}</> : null}
          {description ? <> - {description}</> : null}
        </>
      )
    },
  },
  [NotificationType.EscalationTaskCompleted]: {
    icon: null,
    getMessage: (notification) => {
      const { reason, resolution } = notification.properties
      return (
        <>
          <b>{notification.author?.fullName || 'A user'}</b> marked your escalation as Done{reason ? <>: {reason}</> : null}
          {resolution ? <> - Resolution: {resolution}</> : null}
        </>
      )
    },
  },
  [NotificationType.Generic]: {
    icon: <AlertOutlinedIcon />,
    getMessage: (notification) => notification.properties.text,
  },
}
